/**
 * SSO ticket 换 token 失败的兜底策略工具
 */

import { getSsoLoginUrl } from '@/utils/pathUtil';
// 重试次数限制
const MAX_RETRY_COUNT = 2;
// 重试间隔（毫秒）
const RETRY_INTERVAL = 1500;

/**
 * 错误提示文案（适配不同失败场景）
 * @param {string} errorType 错误类型：network/expired/server/unknown
 * @returns {string} 提示文案
 */
function getErrorMsg(errorType) {
  const msgMap = {
    network: '网络异常，无法完成登录验证，请检查网络后重试',
    expired: '登录凭证已过期，请重新登录',
    server: '登录验证服务异常，请稍后再试',
    unknown: '登录验证失败，请重新登录'
  };
  return msgMap[errorType] || msgMap.unknown;
}

/**
 * 解析错误类型（从接口响应/错误对象中）
 * @param {Error|Object} error 错误对象/接口响应
 * @returns {string} 错误类型
 */
export function parseErrorType(error) {
  // 网络错误（无响应）
  if (!error.response) {
    return 'network';
  }

  const { status, data } = error.response;
  // 401/403 通常是 ticket 过期/无效
  if ([401, 410].includes(status)) {
    return 'expired';
  }
  // 5xx 是服务端错误
  if (status >= 500) {
    return 'server';
  }
  // 自定义业务码（根据实际接口调整）
  if (data?.code === 'TICKET_EXPIRED') {
    return 'expired';
  }
  return 'unknown';
}

/**
 * 显示错误提示（适配 Element UI/原生提示，可根据项目调整）
 * @param {string} msg 提示文案
 * @param {string} type 提示类型：error/warning/info
 */
export function showErrorTip(msg, type = 'error') {
  // 方案1：如果项目用 Element UI
  if (window.$vue?.$message) {
    window.$vue.$message({
      type,
      message: msg,
      duration: 5000,
      showClose: true
    });
  } 
  // 方案2：原生提示（兜底）
  else {
    alert(msg);
  }
}

/**
 * 自动重试 ticket 换 token
 * @param {string} ticket 登录凭证
 * @param {Function} fetchTokenFunc 换取 token 的接口函数
 * @param {number} currentRetry 当前重试次数（默认0）
 * @returns {Promise<Object>} token 响应数据
 */
export async function retryFetchToken(ticket, fetchTokenFunc, currentRetry = 0) {
  // 超过重试次数，抛出错误
  if (currentRetry >= MAX_RETRY_COUNT) {
    const error = new Error('重试次数已达上限');
    error.type = 'retry_limit';
    throw error;
  }

  try {
    // 执行换取 token 接口
    return await fetchTokenFunc(ticket);
  } catch (error) {
    const errorType = parseErrorType(error);
    // 仅对网络异常/服务端错误重试（ticket 过期无需重试）
    if (['network', 'server'].includes(errorType)) {
      showErrorTip(`登录验证失败，${currentRetry + 1}/${MAX_RETRY_COUNT} 重试中...`, 'warning');
      // 延迟重试
      await new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL));
      return retryFetchToken(ticket, fetchTokenFunc, currentRetry + 1);
    }
    // 非重试类错误，直接抛出
    throw error;
  }
}

/**
 * 终极兜底：跳回 SSO 登录页
 * @param {string} ssoLoginUrl SSO 登录地址（从环境变量获取）
 */
export function redirectToSSOLogin() {
  // 清除本地无效的 ticket/token
  localStorage.removeItem('token');
  localStorage.removeItem('ticket');
  
  // 延迟跳转（让用户看到错误提示）
  setTimeout(() => {
    window.location.href = getSsoLoginUrl();
  }, 2000);
}

/**
 * 统一的 ticket 换 token 兜底处理入口
 * @param {string} ticket 登录凭证
 * @param {Function} fetchTokenFunc 换取 token 的接口函数
 * @param {string} getSsoLoginUrl() SSO 登录地址
 */
export async function handleTicketFallback(ticket, fetchTokenFunc) {
  try {
    // 先尝试自动重试
    const res = await retryFetchToken(ticket, fetchTokenFunc);
    // 重试成功，保存 token 并返回
    localStorage.setItem('token', res.data);
    return res;
  } catch (error) {
    // 解析错误类型，显示提示
    const errorType = error.type === 'retry_limit' ? parseErrorType(error) : parseErrorType(error);
    const errorMsg = getErrorMsg(errorType);
    showErrorTip(errorMsg);

    // 终极兜底：跳回 SSO 登录页
    redirectToSSOLogin(getSsoLoginUrl());
    throw new Error(`ticket 换 token 失败：${errorMsg}`);
  }
}